import L from "leaflet";

// Keep the path geometrically between its two markers. Multiple interpolated
// points preserve the existing reveal animation without bending the line away
// from the school and attendance locations.
const buildLinePoints = (from, to, steps = 50) =>
  Array.from({ length: steps + 1 }, (_, index) => {
    const progress = index / steps;
    return [
      from[0] + (to[0] - from[0]) * progress,
      from[1] + (to[1] - from[1]) * progress,
    ];
  });

function animatePolylineReveal(lines, fullLatLngs, duration, onDone) {
  if (!fullLatLngs.length) return null;

  const start = performance.now();
  let rafId = null;
  let finished = false;

  const tick = (now) => {
    const progress = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - progress, 3);
    const count = Math.max(2, Math.ceil(fullLatLngs.length * eased));
    const slice = fullLatLngs.slice(0, count);
    lines.forEach((line) => line.setLatLngs(slice));

    if (progress < 1) {
      rafId = requestAnimationFrame(tick);
    } else if (!finished) {
      finished = true;
      if (onDone) onDone();
    }
  };

  rafId = requestAnimationFrame(tick);
  return () => {
    if (rafId) cancelAnimationFrame(rafId);
  };
}

export const createLineRenderer = (map, renderer = L.canvas({ padding: 0.5 })) => {
  /**
   * @param {[number, number]} from
   * @param {[number, number]} to
   * @param {string} color
   * @param {{ layerGroup?: L.LayerGroup, animate?: boolean, duration?: number, delay?: number, onComplete?: () => void }} [options]
   * @returns {{ layers: L.Layer[], cancelAnimation?: () => void }}
   */
  const drawLine = (from, to, color, options = {}) => {
    const {
      layerGroup = map,
      animate = false,
      duration = 700,
      delay = 0,
      onComplete,
    } = options;

    const latlngs = buildLinePoints(from, to);
    const initial = animate ? [latlngs[0], latlngs[0]] : latlngs;

    const solidLine = L.polyline(initial, {
      color,
      weight: 2.5,
      opacity: 0.95,
      lineCap: "round",
      smoothFactor: 0,
      noClip: true,
      renderer,
    }).addTo(layerGroup);

    const dashLine = L.polyline(initial, {
      color,
      weight: 6,
      opacity: animate ? 0 : 0.35,
      dashArray: "8 12",
      dashOffset: "0",
      lineCap: "round",
      smoothFactor: 0,
      noClip: true,
      renderer,
    }).addTo(layerGroup);

    const layers = [solidLine, dashLine];

    const startDot = L.circleMarker(from, {
      radius: animate ? 6 : 7,
      color,
      fillColor: color,
      fillOpacity: 1,
      weight: 0,
      renderer,
    }).addTo(layerGroup);
    layers.push(startDot);

    const endDot = L.circleMarker(to, {
      radius: animate ? 0 : 7,
      color,
      fillColor: color,
      fillOpacity: 1,
      weight: 0,
      renderer,
    }).addTo(layerGroup);
    layers.push(endDot);

    if (animate) {
      const growEndDot = () => {
        let r = endDot.getRadius();
        const grow = () => {
          r = Math.min(7, r + 0.55);
          endDot.setRadius(r);
          if (r < 7) requestAnimationFrame(grow);
        };
        requestAnimationFrame(grow);
      };
      window.setTimeout(growEndDot, delay + duration * 0.88);
    }

    let cancelAnimation = null;
    if (animate) {
      let revealCancel = null;
      const startReveal = () => {
        revealCancel = animatePolylineReveal([solidLine, dashLine], latlngs, duration, () => {
          dashLine.setStyle({ opacity: 0.35 });
          if (onComplete) onComplete();
        });
      };
      if (delay > 0) {
        const timeoutId = window.setTimeout(startReveal, delay);
        cancelAnimation = () => {
          window.clearTimeout(timeoutId);
          revealCancel?.();
        };
      } else {
        startReveal();
        cancelAnimation = () => revealCancel?.();
      }
    }

    return { layers, cancelAnimation };
  };

  return { drawLine, renderer };
};
